import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown20Regular,
  Search20Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { InfoHint } from "@/components/ui/info-hint";
import { cn } from "@/lib/utils";

const normalize = (s) => String(s ?? "").toLowerCase();

/**
 * MultiCombobox — searchable multi-select dropdown for form fields.
 *
 * Renders selected values as removable chips inside the trigger (so the user
 * sees exactly what's selected without opening the panel). Use this for
 * form fields like "Standort(e)" or "Rolle(n)". For pill-shaped filter
 * triggers, use `<FilterPill>` instead.
 *
 * `options` may be `string[]` or `{ value, label, description? }[]`. `value` is
 * always an array of values (matching the option `value`s). An option with a
 * `description` gets an info icon (in the list and on its chip) whose text
 * shows on hover.
 *
 * `renderChipExtra(opt)` is an optional slot to inject extra UI (e.g. a
 * limit-editor popover) between the chip label and the × remove button.
 * Inner click handlers should `e.stopPropagation()` to avoid toggling the
 * picker.
 *
 * @example
 *   <MultiCombobox
 *     value={selectedRoles}
 *     options={roleOptions}
 *     onChange={setSelectedRoles}
 *     placeholder="Rolle(n) wählen…"
 *   />
 */
export function MultiCombobox({
  value = [],
  options = [],
  onChange,
  placeholder = "Auswählen…",
  searchPlaceholder = "Suchen…",
  emptyMessage = "Keine Ergebnisse",
  disabled = false,
  className,
  renderChipExtra,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState();

  // Wheel-Scroll-Workaround — siehe `combobox.jsx` (gleicher Hintergrund:
  // an `document.body` portaliert damit nichts clipped, dafür Wheel-Scroll
  // direkt am JSX-Element handhaben, weil `react-remove-scroll` von Radix
  // Dialog den nativen Scroll sonst blockiert).
  const handleWheel = (e) => {
    e.currentTarget.scrollTop += e.deltaY;
    e.stopPropagation();
  };

  const items = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "string"
          ? { value: opt, label: opt }
          : {
              value: opt.value,
              label: opt.label ?? String(opt.value),
              description: opt.description || null,
            },
      ),
    [options],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = normalize(search);
    return items.filter((i) => normalize(i.label).includes(q));
  }, [items, search]);

  const selectedItems = useMemo(
    () => items.filter((i) => value.includes(i.value)),
    [items, value],
  );

  useEffect(() => {
    if (!open) {
      setSearch("");
      return;
    }
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  const toggle = (v) => {
    if (value.includes(v)) onChange?.(value.filter((x) => x !== v));
    else onChange?.([...value, v]);
  };

  const removeChip = (e, v) => {
    e.stopPropagation();
    onChange?.(value.filter((x) => x !== v));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={triggerRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => !disabled && setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className={cn(
            "flex w-full items-center justify-between gap-[0.5rem]",
            "min-h-[3.6rem] text-[1.3rem]",
            selectedItems.length > 0
              ? "px-[0.5rem] py-[0.4rem]"
              : "px-[1.2rem] py-[0.4rem]",
            "bg-white border-[0.15rem] border-gray rounded-[0.8rem]",
            "outline-none transition-[border-color,box-shadow] duration-150 cursor-pointer",
            "font-[inherit] text-left",
            open
              ? "border-primary shadow-[0_0_0_0.3rem_rgba(200,212,0,0.15)]"
              : "hover:border-[#d4d4d8]",
            disabled && "bg-bg cursor-not-allowed opacity-60",
            className,
          )}
        >
          {/* Platzhalter auch dann, wenn der Wert nur Einträge enthält, die es
              in den Optionen nicht (mehr) gibt — sonst stünde hier ein leeres
              Feld ohne jeden Hinweis. */}
          {selectedItems.length === 0 ? (
            <span className="text-dim truncate">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap items-center gap-[0.4rem] flex-1 min-w-0">
              {selectedItems.map((opt) => (
                <span
                  key={opt.value}
                  className="inline-flex items-center gap-[0.4rem] px-[0.8rem] py-[0.3rem] rounded-pill bg-[#f3f4f6] text-[#374151] text-[1.15rem] font-medium"
                >
                  <span>{opt.label}</span>
                  <InfoHint text={opt.description} />
                  {renderChipExtra && renderChipExtra(opt)}
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => removeChip(e, opt.value)}
                    aria-label={`${opt.label} entfernen`}
                    className="inline-flex items-center text-[#6b7280] hover:text-foreground cursor-pointer"
                  >
                    <Dismiss16Regular className="w-[1.2rem] h-[1.2rem]" />
                  </span>
                </span>
              ))}
            </div>
          )}
          <ChevronDown20Regular
            className={cn(
              "w-[1.4rem] h-[1.4rem] text-dim flex-shrink-0 transition-transform",
              open && "rotate-180",
            )}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="p-0 overflow-hidden"
        style={{ width: triggerWidth }}
      >
        {items.length > 4 && (
          <div className="px-[0.8rem] pt-[0.8rem] pb-[0.6rem] border-b border-gray">
            <div className="relative">
              <Search20Regular className="absolute left-[0.8rem] top-1/2 -translate-y-1/2 w-[1.4rem] h-[1.4rem] text-[#a1a1aa] pointer-events-none" />
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className={cn(
                  "w-full pl-[2.8rem] pr-[1rem] py-[0.6rem]",
                  "border border-gray rounded-[0.6rem] text-[1.2rem]",
                  "outline-none transition-colors",
                  "placeholder:text-dim font-[inherit]",
                  "focus:border-primary",
                )}
              />
            </div>
          </div>
        )}
        <div
          onWheel={handleWheel}
          className="max-h-[24rem] overflow-y-auto p-[0.4rem]"
        >
          {filtered.length === 0 ? (
            <div className="px-[1.2rem] py-[1rem] text-[1.2rem] text-dim">
              {emptyMessage}
            </div>
          ) : (
            filtered.map((item) => {
              const isSelected = value.includes(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => toggle(item.value)}
                  className={cn(
                    "flex w-full items-center gap-[0.8rem]",
                    "px-[1rem] py-[0.8rem] rounded-[0.6rem]",
                    "text-[1.3rem] text-foreground text-left font-[inherit]",
                    "transition-colors cursor-pointer border-none",
                    isSelected ? "bg-[#fefce8]" : "bg-transparent",
                    !isSelected && "hover:bg-[#f4f4f5]",
                  )}
                >
                  <Checkbox checked={isSelected} />
                  <span className="truncate">{item.label}</span>
                  <InfoHint text={item.description} />
                </button>
              );
            })
          )}
        </div>
        {selectedItems.length > 0 && (
          <div className="border-t border-gray px-[1rem] py-[0.6rem] flex justify-between items-center">
            <span className="text-[1.15rem] text-dim">
              {selectedItems.length} ausgewählt
            </span>
            <button
              type="button"
              onClick={() => onChange?.([])}
              className="bg-transparent border-none cursor-pointer text-[1.15rem] text-primary font-medium font-[inherit] hover:opacity-80 transition-opacity"
            >
              Alle entfernen
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
